import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { seedFullDemoData } from "@/api/base44Client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Database,
  Trash2,
  Search,
  ArrowLeft,
  AlertTriangle,
  Table as TableIcon,
} from "lucide-react";

const ENTITY_NAMES = [
  "AttendanceLog",
  "Closeout",
  "ConsequenceLevel",
  "Content",
  "EAMissTracking",
  "ExcusalSubmission",
  "GradeReport",
  "ManualAssignment",
  "RosterEntry",
  "Settings",
  "Student",
  "SupportTicket",
  "User",
  "Teacher",
];

export default function DataAdmin() {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const queryClient = useQueryClient();

  const filteredTables = ENTITY_NAMES.filter((n) =>
    n.toLowerCase().includes(tableSearch.toLowerCase()),
  );

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ["dataAdmin", selected],
    queryFn: () => base44.entities[selected]?.list() ?? Promise.resolve([]),
    enabled: !!selected,
  });

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some(
        (v) => typeof v === "string" && v.toLowerCase().includes(q),
      ),
    );
  }, [rows, search]);

  const columns = useMemo(() => {
    if (!rows.length) return [];
    const keys = new Set();
    rows.slice(0, 20).forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));
    const preferred = ["id", "student_id", "other_id", "first_name", "last_name", "student_first_name", "student_last_name", "email", "full_name", "grade_level", "status", "attendance_status", "teacher", "content_key", "setting_key", "created_date"];
    const ordered = [...preferred.filter((k) => keys.has(k)), ...Array.from(keys).filter((k) => !preferred.includes(k))];
    return ordered.slice(0, 8);
  }, [rows]);

  const deleteRow = async (id) => {
    if (!confirm("Delete this record?")) return;
    await base44.entities[selected].delete(id);
    refetch();
  };

  const clearTable = async () => {
    if (!confirm(`⚠️ Delete ALL ${rows.length} records in ${selected}? This cannot be undone.`)) return;
    for (const r of rows) {
      await base44.entities[selected].delete(r.id);
    }
    refetch();
    queryClient.invalidateQueries();
  };

  const clearAllData = () => {
    if (!confirm("⚠️ WIPE ALL DATA across every table? This resets the entire app to empty. This cannot be undone.")) return;
    localStorage.removeItem("eagleMockDB_v1");
    // Reseed with empty structure
    const empty = {};
    ENTITY_NAMES.forEach((n) => (empty[n] = []));
    localStorage.setItem("eagleMockDB_v1", JSON.stringify(empty));
    window.location.reload();
  };

  const reseedFresh = () => {
    if (!confirm("Wipe current data and reseed with fresh demo data (728 students, rosters, reports, etc.)?")) return;
    seedFullDemoData();
  };

  // Table list view
  if (!selected) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
                <Database className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Data</h1>
                <p className="text-slate-600 mt-1">View and manage the data stored in your app.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reseedFresh}>
                Reseed Demo Data
              </Button>
              <Button
                onClick={clearAllData}
                className="bg-red-600 hover:bg-red-700 text-white shadow"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Data
              </Button>
            </div>
          </div>

          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <strong>Mocked data:</strong> All records live in your browser&apos;s localStorage. Clearing data here only affects this device. Use <em>Clear All Data</em> for a totally empty start, or <em>Reseed Demo Data</em> to regenerate the sample dataset.
              </div>
            </CardContent>
          </Card>

          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search tables..."
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTables.map((name) => (
              <TableCard
                key={name}
                name={name}
                onClick={() => setSelected(name)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Detail view for a single table
  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setSelected(null)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <TableIcon className="w-5 h-5 text-slate-600" />
                <h1 className="text-2xl font-bold text-slate-900">{selected}</h1>
                <Badge variant="secondary">{rows.length} rows</Badge>
              </div>
              <p className="text-slate-600 text-sm mt-1">
                {isLoading ? "Loading..." : `${filteredRows.length} of ${rows.length} shown`}
              </p>
            </div>
          </div>
          <Button
            onClick={clearTable}
            disabled={!rows.length}
            className="bg-red-600 hover:bg-red-700 text-white shadow disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Table
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search rows..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white"
          />
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {rows.length === 0 && !isLoading ? (
              <div className="p-12 text-center text-slate-500">
                <Database className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No records in this table</p>
                <p className="text-sm mt-1">This table is empty.</p>
              </div>
            ) : (
              <UITable>
                <TableHeader>
                  <TableRow>
                    {columns.map((c) => (
                      <TableHead key={c} className="whitespace-nowrap">{c}</TableHead>
                    ))}
                    <TableHead className="w-20 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.slice(0, 200).map((row) => (
                    <TableRow key={row.id}>
                      {columns.map((c) => (
                        <TableCell key={c} className="max-w-[220px] truncate text-sm">
                          {formatValue(row[c])}
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => deleteRow(row.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </UITable>
            )}
          </CardContent>
        </Card>
        {filteredRows.length > 200 && (
          <p className="text-center text-sm text-slate-500">
            Showing first 200 rows of {filteredRows.length}. Use search to narrow.
          </p>
        )}
      </div>
    </div>
  );
}

function TableCard({ name, onClick }) {
  const { data: rows = [] } = useQuery({
    queryKey: ["dataAdminCount", name],
    queryFn: () => base44.entities[name]?.list() ?? Promise.resolve([]),
  });
  return (
    <button
      onClick={onClick}
      className="group text-left bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-400 hover:shadow-lg transition-all duration-200 flex items-center gap-3"
    >
      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
        <TableIcon className="w-5 h-5 text-slate-600 group-hover:text-blue-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900 truncate">{name}</p>
        <p className="text-xs text-slate-500 mt-0.5">{rows.length} record{rows.length === 1 ? "" : "s"}</p>
      </div>
    </button>
  );
}

function formatValue(v) {
  if (v === null || v === undefined) return <span className="text-slate-300">—</span>;
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "boolean") return v ? "true" : "false";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    try {
      return new Date(s).toLocaleString();
    } catch (e) {}
  }
  return s;
}
